import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideGrid,
  GuideCard,
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';


const SECTION_58_RECORDS_REQUEST = "Request for recorded information: Environmental Information Regulations 2004\n\nLocation: [road name, nearest postcode or what3words, direction of travel, lane]\nIncident date: [date]     Claim reference: [reference]\n\nPlease provide the following recorded information for the location above, covering the 12 months before the incident date and the 3 months after it:\n\n1. The safety inspection policy and inspection frequency that applied to this road, including the investigatory level for potholes.\n2. The dates and results of each safety inspection of this location in the period, including the name of the inspection route or section.\n3. Every defect, enquiry or report recorded for this location in the period, with the date received, the source (inspection, public report, other) and the risk category assigned.\n4. Every repair order raised for this location in the period, with the date raised, target response time and the date the repair was completed.\n5. Where the maintenance or inspection was carried out by a contractor, the instructions issued to the contractor for this location and the records showing they were carried out.\n\nI would prefer the information by email as copies of the original records rather than a summary. If any part of this request would exceed the cost limit, please tell me which part and provide the rest.";

export function Section58DefenceGuide() {
  return (
    <GuideLayout
      slug="section-58-defence"
      category="Legal"
      categoryColor="success"
      title="Section 58 of the Highways Act 1980: The Defence Councils Use"
      seoTitle="Section 58 Highways Act 1980: Pothole Claim Defence | Fixtyer"
      description="What Section 58 of the Highways Act 1980 says, the factors a court weighs, how often councils rely on it to refuse pothole claims, and a template for requesting the records behind it."
      readTime="9 min"
      updatedDate="2026-09-13"
      reviewedDate="2026-07-19"
      datePublished="2026-01-10"
      reviewedFor="England and Wales"
      showCouncilDataCallout
      sources={[
        { name: 'Highways Act 1980 s.58', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58' },
        { name: 'Highways Act 1980 s.41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'RAC FOI analysis (2023 claims)', url: 'https://www.rac.co.uk/drive/news/state-of-the-roads/council-pothole-compensation-claims-double-in-a-year/' },
      ]}
      relatedGuides={[
        { slug: 'how-to-claim-for-pothole-damage', title: 'How to Claim for Pothole Damage', readTime: '12 min' },
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'prior-reports-explained', title: 'Why Prior Reports Are So Important', readTime: '5 min' },
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons', readTime: '6 min' },
      ]}
      atAGlance={[
        { label: 'Answer first', value: 'In England and Wales, Section 58 can provide a highway authority with a defence if it establishes that it took the care reasonably required in the circumstances.' },
        { label: 'What the court weighs', value: 'The character of the road and the traffic expected on it, the standard of maintenance appropriate to it, the state of repair a reasonable person would expect, what the authority knew or could reasonably have been expected to know about the defect, and whether warning notices were displayed.' },
        { label: 'Records to examine', value: 'The authority’s inspection policy, completed inspection records, defect reports, risk assessments and repair history for the location.' },
        { label: 'Prior-report caution', value: 'A third-party report is a lead to verify against authority records; it does not by itself prove receipt, notice or liability.' },
        { label: 'DfT scope', value: 'DfT ratings cover English highway authorities only and are network-level context, not Section 58 evidence for a specific defect.' },
      ]}
      faqs={[
        {
          question: 'What is Section 58 of the Highways Act?',
          answer: 'Section 58 provides a statutory defence for highway authorities in England and Wales. The authority must establish that it took the care reasonably required to secure that the relevant part of the highway was not dangerous for traffic.',
        },
        {
          question: 'What must the council prove to use this defence?',
          answer: 'The court considers the statutory factors and the evidence for the location. Relevant records may include the inspection and maintenance system, whether it was followed, the character and expected use of the highway, and what the authority knew or could reasonably have been expected to know.',
        },
        {
          question: 'How can I assess a Section 58 defence?',
          answer: 'Compare the authority’s stated policy with its completed inspection, defect and repair records for the location. A prior report may be relevant if the authority confirms it received and matched the report to the same defect, but no single item determines the outcome.',
        },
        {
          question: 'Where can I find evidence of prior reports?',
          answer: 'Check FixMyStreet.com for possible leads at or near your location, then ask the authority for its inspection, report and repair records. A FixMyStreet entry is a lead to verify, not proof the council received or matched it to the same defect.',
        },
        {
          question: 'What if the pothole appeared suddenly?',
          answer: 'A defect that arose between reasonable inspections may support the authority’s defence. The result depends on the statutory factors and location-specific evidence, including the nature of the defect and the inspection system actually used.',
        },
        {
          question: 'Does the council have to prove they inspected the road?',
          answer: 'Inspection records are commonly relevant. Missing or incomplete records may be considered alongside the authority’s policy and the other evidence, but they do not determine the outcome on their own.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
        title="Understanding Section 58"
      >
        <p className="mb-4">
          For incidents in England and Wales, a highway authority may rely on Section 58 when responding to a
          pothole damage claim brought under{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41 (their duty to maintain)
          </Link>
          . It is one possible defence and is relevant to many of the{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            most common rejection reasons
          </Link>
          . Understanding the records behind it can help you assess the authority&apos;s response.
        </p>
        <p className="mb-4">
          Keep network condition surveys separate from safety inspections. For example, the{' '}
          <Link to="/councils/dudley" className={inlineLink}>
            Dudley road-maintenance data
          </Link>{' '}
          describes a four-year condition-survey cycle for unclassified roads; that cycle does not
          show when a specific street was safety-inspected.
        </p>
        <GuideTip variant="info" title="The legal position">
          The authority relying on Section 58 must establish the defence. A claimant still needs evidence for
          the incident, damage and alleged breach; independent legal advice may be appropriate.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="What Councils Must Prove"
      >
        <p className="text-brand-200 mb-4">
          Records commonly relevant to a Section 58 defence include:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <span className="font-mono text-accent-400 font-bold text-lg">1</span>
            <div>
              <p className="text-white font-semibold mb-1">Adequate Inspection Systems</p>
              <p className="text-brand-300 text-sm">The published system for inspecting and maintaining the relevant class of road.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">2</span>
            <div>
              <p className="text-white font-semibold mb-1">Systems Were Followed</p>
              <p className="text-brand-300 text-sm">Completed records showing how the system operated at the location and time.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">3</span>
            <div>
              <p className="text-white font-semibold mb-1">Reasonable Response</p>
              <p className="text-brand-300 text-sm">How identified defects were assessed, prioritised and repaired.</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <span className="font-mono text-brand-300 font-bold text-lg">4</span>
            <div>
              <p className="text-white font-semibold mb-1">No Prior Knowledge</p>
              <p className="text-brand-300 text-sm">Reports and inspection findings that may show what was recorded about the specific defect.</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        }
        title="Evidence to compare with the defence"
      >
        <p className="mb-4">
          Compare the response with location-specific records. No category below proves notice, breach or liability
          on its own:
        </p>
        <GuideGrid cols={2}>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            title="Prior Reports"
            variant="accent"
          >
            A possible third-party report can be a useful lead. Verify whether it concerns the same defect and
            whether the authority received it. Read more about{' '}
            <Link to="/guides/prior-reports-explained" className={inlineLink}>
              previously reported
            </Link>
            {' '}and the limits of third-party records.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
            title="Missing Inspections"
          >
            Compare the inspection policy with completed records. A gap may be relevant but must be assessed with
            the other evidence.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            title="Slow Repair Response"
          >
            Check the recorded risk category, target response and completed repair record rather than assuming a
            missed target establishes liability.
          </GuideCard>
          <GuideCard
            icon={
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
            title="Obvious Defects"
          >
            Photographs may help show the defect&apos;s condition, but appearance alone does not establish how long it
            existed or what the authority knew.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
        title="Typical Inspection Frequencies"
      >
        <p className="mb-4">
          Councils set inspection frequencies based on road classification — you can see{' '}
          <Link to="/councils" className={inlineLink}>
            how individual councils perform on road maintenance
          </Link>
          . The figures below are general examples, not legal standards; use the authority&apos;s policy and completed
          records for the road:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-surface-100">
              <tr>
                <th className="text-left p-3 font-semibold text-brand-950">Road Type</th>
                <th className="text-left p-3 font-semibold text-brand-950">Typical Inspection</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 text-surface-700">Motorways / Trunk Roads</td>
                <td className="p-3 text-surface-700">Daily to weekly</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Main A & B Roads</td>
                <td className="p-3 text-surface-700">Monthly</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Residential Streets</td>
                <td className="p-3 text-surface-700">Every 3-12 months</td>
              </tr>
              <tr>
                <td className="p-3 text-surface-700">Rural Roads</td>
                <td className="p-3 text-surface-700">Every 6-12 months</td>
              </tr>
            </tbody>
          </table>
        </div>
        <GuideTip variant="warning" title="Note">
          These are typical frequencies — each council sets its own policy. You can request their policy 
          via Freedom of Information.
        </GuideTip>
      </GuideSection>
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        }
        title="What Section 58 of the Highways Act 1980 actually says"
      >
        <p className="mb-4">
          Section 58(1) provides that, in an action against a highway authority for damage resulting from its
          failure to maintain a highway, it is a defence to prove that the authority had taken such care as in all
          the circumstances was reasonably required to secure that the part of the highway to which the action
          relates was not dangerous for traffic. Section 58(2) lists the matters a court must have regard to:
        </p>
        <ol className="list-decimal pl-6 mb-4 space-y-2 text-surface-700">
          <li>the character of the highway, and the traffic which was reasonably to be expected to use it;</li>
          <li>the standard of maintenance appropriate for a highway of that character and used by such traffic;</li>
          <li>the state of repair in which a reasonable person would have expected to find the highway;</li>
          <li>
            whether the highway authority knew, or could reasonably have been expected to know, that the condition
            of the part of the highway to which the action relates was likely to cause danger to users of the
            highway;
          </li>
          <li>
            where the highway authority could not reasonably have been expected to repair that part of the highway
            before the cause of action arose, what warning notices of its condition had been displayed.
          </li>
        </ol>
        <p className="mb-4">
          The same subsection adds that it is not enough for the authority to show it had arranged for a competent
          person to carry out or supervise the maintenance, unless it also proves it gave that person proper
          instructions and that the instructions were carried out. That is why contractor records can be relevant
          alongside the authority&apos;s own.
        </p>
        <p>
          Read the section in full on{' '}
          <a href="https://www.legislation.gov.uk/ukpga/1980/66/section/58" target="_blank" rel="noopener noreferrer" className={inlineLink}>
            legislation.gov.uk
          </a>
          . The duty it answers is in{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41
          </Link>
          , and the whole claim process is set out in the{' '}
          <Link to="/guides/how-to-claim-for-pothole-damage" className={inlineLink}>
            step-by-step pothole claim guide
          </Link>
          .
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        title="How often councils rely on Section 58"
      >
        <p className="mb-4">
          The RAC&apos;s Freedom of Information analysis of 2023 claims, published in 2024, covered 21 English councils.
          The 17 councils that paid any compensation paid 3,131 of 20,432 claims (15%). Of the nine councils that
          gave reasons for refusal, 6,028 of 8,172 refused claims (74%) were refused on the basis that the council
          did not know the pothole existed. That is the knowledge question in Section 58(2), and it is the most
          common reason a pothole claim in England and Wales is turned down.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
            <p className="font-display text-2xl font-bold text-brand-950">15%</p>
            <p className="text-sm text-surface-700">of 20,432 claims paid by 17 councils in 2023</p>
          </div>
          <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
            <p className="font-display text-2xl font-bold text-brand-950">74%</p>
            <p className="text-sm text-surface-700">of refusals (nine councils) on the basis the council had no prior knowledge</p>
          </div>
          <div className="p-4 bg-surface-50 rounded-xl border border-surface-200">
            <p className="font-display text-2xl font-bold text-brand-950">£260</p>
            <p className="text-sm text-surface-700">RAC estimate of the average payout</p>
          </div>
        </div>
        <p className="text-sm text-surface-600">
          Source:{' '}
          <a href="https://www.rac.co.uk/drive/news/state-of-the-roads/council-pothole-compensation-claims-double-in-a-year/" target="_blank" rel="noopener noreferrer" className={inlineLink}>
            RAC, council pothole compensation claims double in a year
          </a>
          . The figures are for the councils that answered and are not a national rate. They do not say anything
          about a specific road, which is why the records for the location matter more than the averages.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        }
        title="Ask for the records behind the defence: a short request template"
      >
        <p className="mb-4">
          A refusal letter that cites Section 58 usually rests on inspection and defect records you have not seen.
          You can ask for them. Road-condition and maintenance records are normally handled under the Environmental
          Information Regulations 2004, and the authority has 20 working days to respond. Send the request to the
          authority&apos;s information or FOI team, quoting your claim reference, and keep the request and the response
          together with your claim file.
        </p>
        <pre className="whitespace-pre-wrap rounded-xl border border-surface-200 bg-surface-50 p-4 text-sm text-surface-800 mb-4">{SECTION_58_RECORDS_REQUEST}</pre>
        <p className="mb-4">
          When the records arrive, compare them with the refusal letter and the authority&apos;s stated policy. Look
          at the last inspection date, whether anything was recorded at the location before your incident, and how
          any recorded defect was categorised and repaired. A gap between policy and practice is something to put
          back to the authority; it is not, on its own, a finding of liability.
        </p>
        <p>
          The{' '}
          <Link to="/guides/request-road-inspection-records" className={inlineLink}>
            full inspection-records guide
          </Link>{' '}
          has longer EIR and FOI templates and explains what to do if the authority refuses or charges. If you
          decide to go further, the{' '}
          <Link to="/guides/pothole-claim-rejected-next-steps" className={inlineLink}>
            rejected claim guide
          </Link>{' '}
          and the{' '}
          <Link to="/guides/small-claims-court-guide" className={inlineLink}>
            small claims court guide
          </Link>{' '}
          set out the next steps.
        </p>
      </GuideSection>
    </GuideLayout>
  );
}
